import React, { useEffect, useState } from 'react'

import { makeStyles, Theme, Typography } from '@material-ui/core'

import { useUser } from '../../hooks/useUser'
import { useBilling, ValidSortField } from '../../hooks/useBilling'
import { Container } from '../../components'
import { BillingSummary } from './BillingSummary'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../constants'
import { InternalInvoice } from '../../models/Invoice'
import { BillingInvoiceTable } from './BillingInvoiceTable'
import { ViewInvoice } from './ViewInvoice'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    title: {
        flex: 1,
        justifyContent: 'center',
    },
    summaryContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 100,
    },
    tableContainer: {
        flex: '1 1 auto',
        overflow: 'hidden',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
}))

export const Billing = () => {
    const { workspaceUser } = useUser()
    const classes = useStyles()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [sortField, setSortField] = useState<ValidSortField>('issue_date')
    const [order, setOrder] = useState<'asc' | 'desc'>('desc')
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
        null,
    )
    const propertyId = workspaceUser?.active_workspace?.id

    const { summaryData, isLoading, data } = useBilling({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort: order === 'desc' ? `-${sortField}` : sortField,
        property_id: propertyId,
    })
    const handleRequestSort = (
        property: keyof InternalInvoice,
        newOrder: 'asc' | 'desc',
    ) => {
        setSortField(property as ValidSortField)
        setOrder(newOrder)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const urlPage = parseInt(params.get('page') || '0')
        const urlPageSize = parseInt(params.get('pageSize') || '50')
        setPage(urlPage)
        setRowsPerPage(urlPageSize)
    }, [])

    const selectedInvoice =
        data?.data.results.find(
            (invoice) => invoice.id === selectedInvoiceId,
        ) || null

    return (
        <Container className={classes.root}>
            {/* Title */}
            <Container className={classes.title}>
                <Typography variant="h5">Billing</Typography>
            </Container>
            {/* Summary Section */}
            <Container className={classes.summaryContainer}>
                <BillingSummary
                    summaryData={summaryData}
                    isLoading={isLoading}
                />
            </Container>
            {/* Invoice Table */}
            <Container className={classes.tableContainer}>
                <BillingInvoiceTable
                    invoices={data?.data.results}
                    orderBy={sortField as keyof InternalInvoice}
                    order={order}
                    onRequestSort={handleRequestSort}
                    onViewInvoice={(invoice) => {
                        setSelectedInvoiceId(invoice.id)
                    }}
                    tableHeight="100%"
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={data?.data.count || 0}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Container>
            <ViewInvoice
                invoice={selectedInvoice}
                onClose={() => setSelectedInvoiceId(null)}
            />
        </Container>
    )
}
